import React, { useState, useEffect } from 'react';
import settlementService from './footballSettlement';

const SettlementViewer = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [settlements, setSettlements] = useState(null);
  const [stats, setStats] = useState(null);
  const [leagueStats, setLeagueStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pullStatus, setPullStatus] = useState(null);

  useEffect(() => {
    loadSettlements();
  }, [selectedDate]);

  const loadSettlements = async () => {
    setLoading(true);
    try {
      const data = await settlementService.getSettlementsByDate(selectedDate);
      const dayStats = await settlementService.getWinLossRecordForDate(selectedDate);
      const leagues = await settlementService.getLeagueStatsForDate(selectedDate);

      setSettlements(data);
      setStats(dayStats);
      setLeagueStats(leagues);
    } catch (error) {
      console.error('Error loading settlements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualPull = async (type) => {
    setPullStatus({ type, status: 'running' });
    try {
      const result = await settlementService.runManualPull(type, selectedDate);
      setPullStatus({ type, status: 'success', result });
      await loadSettlements(); // Reload data
    } catch (error) {
      setPullStatus({ type, status: 'error', error: error.message });
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h1 className="text-2xl font-bold mb-4">Football Settlement Viewer</h1>

        <div className="flex items-center gap-4 mb-6">
          <input
            type="date"
            value={formatDate(selectedDate)}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="px-3 py-2 border rounded-md"
          />
          <button
            onClick={() => handleManualPull('morning')}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            disabled={pullStatus?.status === 'running'}
          >
            Run Morning Pull
          </button>
          <button
            onClick={() => handleManualPull('nightly')}
            className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600"
            disabled={pullStatus?.status === 'running'}
          >
            Run Nightly Settlement
          </button>
          <button
            onClick={loadSettlements}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Refresh
          </button>
        </div>

        {pullStatus && (
          <div className={`p-4 rounded-md mb-4 ${
            pullStatus.status === 'running' ? 'bg-yellow-100' :
            pullStatus.status === 'success' ? 'bg-green-100' :
            'bg-red-100'
          }`}>
            <div className="font-semibold">
              {pullStatus.type === 'morning' ? '🌅 Morning Pull' : '🌙 Nightly Settlement'}
            </div>
            {pullStatus.status === 'running' && <div>Processing...</div>}
            {pullStatus.status === 'success' && (
              <div>
                ✅ Success! Processed {pullStatus.result.fixtureCount} fixtures
                {pullStatus.result.settledCount !== undefined && (
                  <div>Settled: {pullStatus.result.settledCount}</div>
                )}
              </div>
            )}
            {pullStatus.status === 'error' && (
              <div>❌ Error: {pullStatus.error}</div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
          {stats && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">Day Statistics</h2>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.wins}</div>
                  <div className="text-sm text-gray-600">Home Wins</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{stats.losses}</div>
                  <div className="text-sm text-gray-600">Away Wins</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{stats.draws}</div>
                  <div className="text-sm text-gray-600">Draws</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{stats.scheduled}</div>
                  <div className="text-sm text-gray-600">Scheduled</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{stats.live}</div>
                  <div className="text-sm text-gray-600">Live</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{stats.total}</div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
              </div>
            </div>
          )}

          {leagueStats && Object.keys(leagueStats).length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">League Breakdown</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">League</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Games</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Settled</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">H-W</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Draw</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">A-W</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Goals</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {Object.entries(leagueStats)
                      .sort((a, b) => b[1].games - a[1].games)
                      .map(([leagueId, stats]) => (
                        <tr key={leagueId} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <div className="text-sm font-medium text-gray-900">{stats.name}</div>
                            <div className="text-xs text-gray-500">{stats.country}</div>
                          </td>
                          <td className="px-4 py-2 text-center text-sm">{stats.games || 0}</td>
                          <td className="px-4 py-2 text-center text-sm">{stats.settled || 0}</td>
                          <td className="px-4 py-2 text-center text-sm text-green-600">{stats.homeWins || 0}</td>
                          <td className="px-4 py-2 text-center text-sm text-yellow-600">{stats.draws || 0}</td>
                          <td className="px-4 py-2 text-center text-sm text-red-600">{stats.awayWins || 0}</td>
                          <td className="px-4 py-2 text-center text-sm">{isNaN(stats.totalGoals) ? 0 : stats.totalGoals}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {settlements && Object.keys(settlements).length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Game Details</h2>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {Object.values(settlements)
                  .sort((a, b) => a.timestamp - b.timestamp)
                  .map((game) => (
                    <div key={game.gameId} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex-1">
                        <div className="font-medium">
                          {game.teams.home.name} vs {game.teams.away.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {game.league.name} • {game.league.country}
                        </div>
                      </div>
                      <div className="text-center px-4">
                        {game.result === 'SCHEDULED' && (
                          <span className="text-blue-600">Scheduled</span>
                        )}
                        {game.result === 'LIVE' && (
                          <span className="text-purple-600 font-bold">LIVE</span>
                        )}
                        {['W', 'L', 'D'].includes(game.result) && (
                          <div>
                            <div className="font-bold">
                              {game.score.home} - {game.score.away}
                            </div>
                            <div className={`text-xs ${
                              game.result === 'W' ? 'text-green-600' :
                              game.result === 'L' ? 'text-red-600' :
                              'text-yellow-600'
                            }`}>
                              {game.result === 'W' ? 'Home Win' :
                               game.result === 'L' ? 'Away Win' : 'Draw'}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">
                        ID: {game.gameId}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SettlementViewer;